import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TaskSquare, Calendar, Clock, DocumentText1, Math, TickCircle, CloseCircle, Box1, Edit2 } from "iconsax-react";
import cn from "classnames";
import { ModalWrapper, Badge, Skeleton, Button, Prompt, Toast, Dropdown, DropdownItem, PromptForm, ScrollArea } from "@/shared/ui";
import { MoreIcon } from "@/shared/assets/icons";
import { formatDateForDisplay, extractErrorMessage, formatMoneyKzt } from "@/shared/utils";
import { SigningStatus } from "@/shared/components";
import { useCancelItemRequestMutation } from "@/features/accounting/warehouse";
import { getAdvanceStatusConfig } from "@/features/accounting/advances";
import {
  useDeleteAnnualLeaveMutation,
  useDeleteUnpaidLeaveMutation,
  useDeleteMedicalLeaveMutation,
} from "@/features/hr/leave-applications";
import { useDeleteSelfAmendmentMutation } from "@/features/self/shared";
import { SectionDiffRenderer } from "@/features/self/profile/components/personal-sheet";
import {
  useAcknowledgeAnnualOrderMutation,
  useAcknowledgeUnpaidOrderMutation,
  useAcknowledgeMedicalOrderMutation,
  useAcknowledgeResignationOrderMutation,
  useDeleteSelfResignationMutation,
  useCancelDataChangeRequestMutation,
  useDeleteSelfAdvanceMutation,
} from "../api";
import {
  canAcknowledgeOrder,
  canEditRequest,
  canDeleteAdvanceRequest,
  canDeleteLeaveRequest,
  canEditAdvanceRequest,
  isRevisionRequested,
} from "../utils/selfRequestRules";
import { getStatusBadgeColor, getStatusIcon, resolveLeaveRequestStatusLabel } from "../../shared/utils/statusUtils";
import type { SelfServiceRequest } from "../types";
import RequestAuditTimeline from "./RequestAuditTimeline";
import EditSelfLeaveForm from "./EditSelfLeaveForm";
import EditSelfAmendmentForm from "./EditSelfAmendmentForm";
import EditSelfResignationForm from "./EditSelfResignationForm";
import EditSelfAdvanceForm from "./EditSelfAdvanceForm";

interface Props {
  request: SelfServiceRequest | null;
  onClose: () => void;
  onSuccess?: () => void;
  isLoading?: boolean;
  readOnly?: boolean;
}

function RequestDetailsSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-6 min-h-0 flex-1">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 pb-3 border-b surface-base-stroke">
          <Skeleton height={24} width={200} />
          <Skeleton height={32} width={150} />
        </div>
        <div className="flex flex-col">
          {Array.from({ length: 5 }).map((_, idx) => (
            <div key={idx} className={cn("py-3 flex items-center gap-3", idx < 4 && "border-b surface-base-stroke")}>
              <Skeleton height={16} width={16} />
              <Skeleton height={16} width={120} />
              <Skeleton height={16} width={180} className="ml-auto" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-3 border-l surface-base-stroke pl-6">
        <Skeleton height={20} width={80} />
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="flex gap-3">
            <Skeleton circle width={10} height={10} />
            <div className="flex-1 flex flex-col gap-1">
              <Skeleton height={12} width={100} />
              <Skeleton height={14} width={140} />
              <Skeleton height={12} width="60%" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface RequestField {
  key: string;
  label: string;
  value: string;
  icon: typeof Calendar;
  isStatus?: boolean;
  statusComponent?: React.ReactNode;
}

export default function RequestDetailsModal({ request, onClose, onSuccess, isLoading, readOnly }: Props) {
  const { t, i18n } = useTranslation("EmployeePortal");
  const { t: tAdvances } = useTranslation("AdvancesPage");
  const locale = i18n.language as "ru" | "kk" | "en";
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant?: "success" | "error" } | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmCancelDataChange, setConfirmCancelDataChange] = useState(false);

  const [acknowledgeAnnual, { isLoading: isAcknowledgingAnnual }] = useAcknowledgeAnnualOrderMutation();
  const [acknowledgeUnpaid, { isLoading: isAcknowledgingUnpaid }] = useAcknowledgeUnpaidOrderMutation();
  const [acknowledgeMedical, { isLoading: isAcknowledgingMedical }] = useAcknowledgeMedicalOrderMutation();
  const [acknowledgeResignation, { isLoading: isAcknowledgingResignation }] = useAcknowledgeResignationOrderMutation();

  const [cancelItemRequest, { isLoading: isCancellingItemRequest }] = useCancelItemRequestMutation();
  const [cancelDataChange, { isLoading: isCancellingDataChange }] = useCancelDataChangeRequestMutation();
  const [deleteAnnual] = useDeleteAnnualLeaveMutation();
  const [deleteUnpaid] = useDeleteUnpaidLeaveMutation();
  const [deleteMedical] = useDeleteMedicalLeaveMutation();
  const [deleteAmendment] = useDeleteSelfAmendmentMutation();
  const [deleteResignation] = useDeleteSelfResignationMutation();
  const [deleteSelfAdvance] = useDeleteSelfAdvanceMutation();

  const isAcknowledging =
    isAcknowledgingAnnual || isAcknowledgingUnpaid || isAcknowledgingMedical || isAcknowledgingResignation;

  const requestFields = useMemo(() => {
    if (!request) return [];

    const fields: RequestField[] = [];

    const revisionActive = isRevisionRequested(request);
    const STATUS_LABEL_MAP: Record<string, string> = {
      draft: t("requests.status.draft"),
      signing_pending: t("requests.status.pending"),
      app_approved: t("requests.status.approved"),
      order_pending: t("requests.status.orderPending"),
      order_uploaded: t("requests.status.orderUploaded"),
      order_acknowledged: t("requests.status.orderUploaded"),
      pending: t("requests.status.pending"),
      approved: t("requests.status.approved"),
      fulfilling: t("requests.status.fulfilling"),
      active: t("requests.status.active"),
      agr_pending: t("requests.status.pending"),
      pending_review: t("requests.status.pending"),
      pending_signing: t("requests.status.pending"),
      completed: t("requests.status.completed"),
      cancelled: t("requests.status.cancelled"),
      fulfilled: t("requests.status.fulfilled"),
      rejected: t("requests.status.rejected"),
      applied: t("requests.status.applied"),
      signed: t("requests.status.signed"),
      paid: t("requests.status.paid"),
      agreement_signed: t("requests.status.signed"),
      revoked: t("requests.status.cancelled"),
      pending_hr: t("requests.status.pendingHr"),
      hr_approved: t("requests.status.hrApproved"),
      hr_rejected: t("requests.status.rejected"),
      chief_rejected: t("requests.status.rejected"),
    };
    function resolveStatusLabel(): string {
      if (!request) return "";
      if (revisionActive) return t("requests.status.revisionRequested");
      const leaveLabel = resolveLeaveRequestStatusLabel(
        request.type,
        request.status,
        request.date_range?.start_date,
        t,
      );
      if (leaveLabel) return leaveLabel;
      if (request.status_label) {
        const label = request.status_label[locale] || request.status_label.ru;
        if (label && label !== request.status) return label;
      }
      return STATUS_LABEL_MAP[request.status] || request.status;
    }
    const advanceConfig =
      request.type === "advance" || request.type === "business_trip" ? getAdvanceStatusConfig(request.status) : null;
    const statusLabel = advanceConfig
      ? revisionActive
        ? tAdvances("statusLabels.revision")
        : tAdvances(advanceConfig.label)
      : resolveStatusLabel();
    const badgeColor = advanceConfig
      ? revisionActive
        ? "notice"
        : advanceConfig.color
      : revisionActive
        ? "notice"
        : getStatusBadgeColor(request.status);
    const badgeIcon = advanceConfig
      ? revisionActive
        ? <Edit2 size={14} color="currentColor" />
        : advanceConfig.icon
      : revisionActive
        ? <Edit2 size={16} color="currentColor" />
        : getStatusIcon(request.status);
    fields.push({
      key: "status",
      label: t("requests.details.status"),
      value: "",
      icon: Clock,
      isStatus: true,
      statusComponent: <Badge variant="soft" color={badgeColor} text={statusLabel} icon={badgeIcon} />,
    });

    const typeLabel = request.type_label
      ? request.type_label[locale] || request.type_label.ru || request.type
      : request.type;
    fields.push({
      key: "type",
      label: t("requests.details.type"),
      value: typeLabel,
      icon: TaskSquare,
    });

    fields.push({
      key: "createdAt",
      label: t("requests.details.createdAt"),
      value: formatDateForDisplay(request.created_at),
      icon: Calendar,
    });

    if (request.date_range) {
      const startDate = formatDateForDisplay(request.date_range.start_date);
      const endDate = formatDateForDisplay(request.date_range.end_date);
      const daysCount = request.date_range.days_count
        ? ` (${request.date_range.days_count} ${request.date_range.days_count === 1 ? t("requests.details.day") : t("requests.details.days")})`
        : "";
      fields.push({
        key: "dateRange",
        label: t("requests.details.dateRange"),
        value: `${startDate} - ${endDate}${daysCount}`,
        icon: Calendar,
      });
    }

    if (request.details) {
      if (request.details.description) {
        fields.push({
          key: "description",
          label: t("requests.details.description"),
          value: String(request.details.description),
          icon: DocumentText1,
        });
      }

      if (request.details.reason) {
        fields.push({
          key: "reason",
          label: t("requests.details.reason"),
          value: String(request.details.reason),
          icon: DocumentText1,
        });
      }

      if (request.details.diagnosis) {
        fields.push({
          key: "diagnosis",
          label: t("requests.details.diagnosis"),
          value: String(request.details.diagnosis),
          icon: DocumentText1,
        });
      }

      if (request.details.last_working_day) {
        fields.push({
          key: "lastWorkingDay",
          label: t("requests.details.lastWorkingDay"),
          value: formatDateForDisplay(request.details.last_working_day),
          icon: Calendar,
        });
      }

      if (request.type === "amendment" && request.details.effective_date) {
        fields.push({
          key: "effectiveDate",
          label: t("requests.details.effectiveDate"),
          value: formatDateForDisplay(String(request.details.effective_date)),
          icon: Calendar,
        });
      }

      if (request.details.amount) {
        const isPercentAdvance =
          request.type === "advance" &&
          request.details.amount_mode === "percent" &&
          request.details.percent != null;
        fields.push({
          key: "amount",
          label: t("requests.details.amount"),
          value: isPercentAdvance
            ? t("requests.details.amountPercent", {
                percent: String(Number(request.details.percent)),
                amount: formatMoneyKzt(request.details.amount),
              })
            : formatMoneyKzt(request.details.amount),
          icon: DocumentText1,
        });
      }

      if (request.type === "completion_act") {
        if (request.details.payment_status) {
          fields.push({
            key: "paymentStatus",
            label: t("requests.details.paymentStatus"),
            value: String(request.details.payment_status),
            icon: DocumentText1,
          });
        }

        if (request.details.net_amount !== null && request.details.net_amount !== undefined) {
          fields.push({
            key: "netAmount",
            label: t("requests.details.netAmount"),
            value: formatMoneyKzt(request.details.net_amount),
            icon: Math,
          });
        }
      }

      if (request.type === "unit_request") {
        if (request.details.item_name) {
          fields.push({
            key: "itemName",
            label: t("requests.details.itemName"),
            value: String(request.details.item_name),
            icon: DocumentText1,
          });
        }
        if (request.details.quantity) {
          fields.push({
            key: "quantity",
            label: t("requests.details.quantity"),
            value: String(request.details.quantity),
            icon: Math,
          });
        }
        if (request.details.rejection_reason) {
          fields.push({
            key: "rejectionReason",
            label: t("requests.details.rejectionReason"),
            value: String(request.details.rejection_reason),
            icon: DocumentText1,
          });
        }
      }

      if (request.type === "data_change_request") {
        if (request.details.surname) {
          fields.push({
            key: "surname",
            label: t("requests.dataChange.surname"),
            value: String(request.details.surname),
            icon: DocumentText1,
          });
        }
        if (request.details.name) {
          fields.push({
            key: "name",
            label: t("requests.dataChange.name"),
            value: String(request.details.name),
            icon: DocumentText1,
          });
        }
        if (request.details.father_name !== undefined) {
          fields.push({
            key: "fatherName",
            label: t("requests.dataChange.fatherName"),
            value: String(request.details.father_name || "—"),
            icon: DocumentText1,
          });
        }
        if (request.details.national_id_number) {
          fields.push({
            key: "idNumber",
            label: t("requests.dataChange.idNumber"),
            value: String(request.details.national_id_number),
            icon: DocumentText1,
          });
        }
        if (request.details.national_id_issue_date) {
          fields.push({
            key: "issueDate",
            label: t("requests.dataChange.issueDate"),
            value: formatDateForDisplay(String(request.details.national_id_issue_date)),
            icon: Calendar,
          });
        }
        if (request.details.national_id_expiry_date) {
          fields.push({
            key: "expiryDate",
            label: t("requests.dataChange.expiryDate"),
            value: formatDateForDisplay(String(request.details.national_id_expiry_date)),
            icon: Calendar,
          });
        }
        if (request.details.national_id_issued_by) {
          fields.push({
            key: "issuedBy",
            label: t("requests.dataChange.issuedBy"),
            value: String(request.details.national_id_issued_by),
            icon: DocumentText1,
          });
        }
        if (request.details.rejection_reason) {
          fields.push({
            key: "rejectionReason",
            label: t("requests.details.rejectionReason"),
            value: String(request.details.rejection_reason),
            icon: DocumentText1,
          });
        }
        if (request.details.hr_approved_at) {
          fields.push({
            key: "hrApprovedAt",
            label: t("requests.dataChange.hrApprovedAt"),
            value: formatDateForDisplay(String(request.details.hr_approved_at)),
            icon: TickCircle,
          });
        }
        if (request.details.chief_approved_at) {
          fields.push({
            key: "chiefApprovedAt",
            label: t("requests.dataChange.chiefApprovedAt"),
            value: formatDateForDisplay(String(request.details.chief_approved_at)),
            icon: TickCircle,
          });
        }
      }

      if (request.type === "advance") {
        if (request.details.kind) {
          fields.push({
            key: "advanceKind",
            label: t("requests.details.advanceKind"),
            value: request.details.kind === "salary" ? t("advance.kindSalary") : t("advance.kindEmployee"),
            icon: DocumentText1,
          });
        }
        if (request.details.kind === "employee") {
          if (request.details.document_number) {
            fields.push({
              key: "advanceDocNumber",
              label: t("advance.fields.documentNumber"),
              value: String(request.details.document_number),
              icon: DocumentText1,
            });
          }
        }
      }

      if (
        request.details.signing_status !== null &&
        request.details.signing_status !== undefined &&
        typeof request.details.signing_status === "number"
      ) {
        fields.push({
          key: "signingStatus",
          label: t("requests.details.signingStatus"),
          value: "",
          icon: Clock,
          isStatus: true,
          statusComponent: <SigningStatus signingStatus={request.details.signing_status} locale={locale} />,
        });
      }
    }

    if (revisionActive && request.application_review_note) {
      fields.push({
        key: "revisionNote",
        label: t("requests.details.revisionNote"),
        value: request.application_review_note,
        icon: DocumentText1,
      });
    }

    return fields;
  }, [request, locale, t]);

  async function handleAcknowledgeOrder() {
    if (!request || isAcknowledging) return;

    try {
      if (request.type === "annual_leave") {
        await acknowledgeAnnual(request.id).unwrap();
      } else if (request.type === "unpaid_leave") {
        await acknowledgeUnpaid(request.id).unwrap();
      } else if (request.type === "medical_leave") {
        await acknowledgeMedical(request.id).unwrap();
      } else if (request.type === "resignation") {
        await acknowledgeResignation(request.id).unwrap();
      } else {
        return;
      }

      onSuccess?.();
      setPrompt({
        title: t("requests.acknowledgeOrderSuccess"),
        text: t("requests.acknowledgeOrderSuccessMessage"),
        variant: "success",
      });
    } catch (error) {
      setPrompt({
        title: t("requests.acknowledgeOrderError"),
        text: extractErrorMessage(error, "requests.acknowledgeOrderErrorMessage", t),
        variant: "error",
      });
    }
  }

  function handlePromptClose() {
    const isSuccess = prompt?.variant === "success";
    setPrompt(null);
    if (isSuccess) {
      onClose();
    }
  }

  async function handleCancelItemRequest() {
    if (!request || isCancellingItemRequest) return;
    try {
      await cancelItemRequest(request.id).unwrap();
      onSuccess?.();
      setPrompt({
        title: t("requests.cancelItemRequestSuccess"),
        text: t("requests.cancelItemRequestSuccessMessage"),
        variant: "success",
      });
    } catch (error) {
      setPrompt({
        title: t("requests.cancelItemRequestError"),
        text: extractErrorMessage(error, "requests.cancelItemRequestErrorMessage", t),
        variant: "error",
      });
    }
  }

  function canCancelItemRequest(req: SelfServiceRequest): boolean {
    return req.type === "unit_request" && req.status === "pending";
  }

  async function handleCancelDataChange() {
    if (!request || isCancellingDataChange) return;
    try {
      await cancelDataChange(request.id).unwrap();
      setConfirmCancelDataChange(false);
      onSuccess?.();
      setPrompt({
        title: t("requests.cancelDataChangeSuccess"),
        text: t("requests.cancelDataChangeSuccessMessage"),
        variant: "success",
      });
    } catch (error) {
      setConfirmCancelDataChange(false);
      const status = (error as { status?: number })?.status;
      const text =
        status === 400 || status === 404
          ? t("requests.cancelDataChangeStaleError")
          : extractErrorMessage(error, "requests.cancelDataChangeErrorMessage", t);
      setPrompt({ title: t("requests.cancelDataChangeError"), text, variant: "error" });
    }
  }

  function canCancelDataChangeRequest(req: SelfServiceRequest): boolean {
    return (
      req.type === "data_change_request" &&
      req.details?.initiated_by === "worker" &&
      (req.status === "pending_hr" || req.status === "hr_approved")
    );
  }

  function filterField(field: RequestField): boolean {
    return !!(field.value || field.isStatus);
  }

  function renderField(field: RequestField, index: number, array: RequestField[]) {
    const IconComponent = field.icon;
    return (
      <div
        key={field.key}
        className={cn("py-3 flex items-center gap-3", index < array.length - 1 && "border-b surface-base-stroke")}>
        <span className="content-action-brand">
          <IconComponent size={16} color="currentColor" />
        </span>
        <span className="text-body-regular-md content-base-secondary flex-1 min-w-[108px]">{field.label}</span>
        {field.isStatus ? (
          <div className="flex justify-end">{field.statusComponent}</div>
        ) : (
          <p className="text-body-bold-md content-base-primary text-right">{field.value}</p>
        )}
      </div>
    );
  }

  if (isLoading || !request) {
    return (
      <ModalWrapper onClose={onClose} width="w-5xl">
        <h4 className="text-display-2xs content-base-primary">{t("requests.details.title")}</h4>
        <div className="mt-4">
          <RequestDetailsSkeleton />
        </div>
      </ModalWrapper>
    );
  }

  const typeLabel = request.type_label
    ? request.type_label[locale] || request.type_label.ru || request.type
    : request.type;

  const filteredFields = requestFields.filter(filterField);

  const isNewFormatDataChange =
    request.type === "data_change_request" &&
    request.details?.changed_sections != null &&
    request.details.changed_sections.length > 0;

  function handleEditClick() {
    setMenuOpen(false);
    setShowEditForm(true);
  }

  async function handleDelete() {
    if (!request || isDeleting) return;
    try {
      setIsDeleting(true);
      if (request.type === "annual_leave") await deleteAnnual(request.id).unwrap();
      else if (request.type === "unpaid_leave") await deleteUnpaid(request.id).unwrap();
      else if (request.type === "medical_leave") await deleteMedical(request.id).unwrap();
      else if (request.type === "amendment") await deleteAmendment(request.id).unwrap();
      else if (request.type === "resignation") await deleteResignation(request.id).unwrap();
      else if (request.type === "advance") await deleteSelfAdvance(request.id).unwrap();
      setConfirmDelete(false);
      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      setIsDeleting(false);
      setConfirmDelete(false);
      setPrompt({
        title: t("requests.deleteError"),
        text: extractErrorMessage(err, "requests.deleteError", t),
        variant: "error",
      });
    }
  }

  if (showEditForm && request && !readOnly) {
    const editFormProps = {
      request,
      onClose() {
        setShowEditForm(false);
      },
      onSuccess() {
        setShowEditForm(false);
        onSuccess?.();
        onClose();
      },
    };

    if (request.type === "amendment") {
      return <EditSelfAmendmentForm {...editFormProps} />;
    }
    if (request.type === "resignation") {
      return <EditSelfResignationForm {...editFormProps} />;
    }
    if (request.type === "advance") {
      return <EditSelfAdvanceForm {...editFormProps} />;
    }
    return <EditSelfLeaveForm {...editFormProps} />;
  }

  return (
    <ModalWrapper onClose={onClose} width="w-5xl">
      <h4 className="text-display-2xs content-base-primary">{t("requests.details.title")}</h4>

      {!readOnly && (canEditRequest(request) || canDeleteAdvanceRequest(request) || canDeleteLeaveRequest(request)) && (
        <div className="absolute top-8 right-[72px]">
          <Dropdown open={menuOpen} onClose={() => setMenuOpen(false)} align="right">
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="btn btn-tertiary btn-icon w-8! h-8! p-0! flex items-center justify-center radius-xs content-base-secondary! cursor-pointer">
              <MoreIcon size={20} />
            </button>
            {canEditRequest(request) && (
              <DropdownItem onClick={handleEditClick}>{t("requests.editLeave")}</DropdownItem>
            )}
            <DropdownItem
              onClick={() => {
                setMenuOpen(false);
                setConfirmDelete(true);
              }}>
              {t("requests.deleteLeave")}
            </DropdownItem>
          </Dropdown>
        </div>
      )}

      <div className="grid grid-cols-2 mt-4 min-h-0 flex-1 overflow-hidden">
        <ScrollArea className="min-h-0">
          <div className="flex flex-col pr-6">
          <div className="flex flex-col gap-2 mb-4 border-b surface-base-stroke pb-3">
            <p className="text-body-bold-lg content-base-primary">{typeLabel}</p>
          </div>

          <div className="flex flex-col">{filteredFields.map(renderField)}</div>

          {isNewFormatDataChange && request.details && (
            <div className="mt-4">
              <SectionDiffRenderer
                previousValues={(request.details.old_values ?? request.details.current_values ?? {}) as Record<string, unknown>}
                requestedChanges={(request.details.requested_changes ?? {}) as Record<string, unknown>}
                changedSections={request.details.changed_sections as string[]}
                uploadedFilesUrls={request.details.uploaded_files_urls}
              />
            </div>
          )}

          {request.type === "unit_request" &&
            request.details?.request_units &&
            request.details.request_units.length > 0 && (
              <div className="mt-4 p-5 radius-lg border surface-component-stroke flex flex-col gap-3">
                <div className="flex items-center gap-2 pb-2 border-b surface-base-stroke">
                  <div className="w-8 aspect-square surface-component-fill radius-xs flex items-center justify-center">
                    <span className="content-action-neutral">
                      <Box1 size={16} color="currentColor" />
                    </span>
                  </div>
                  <h5 className="text-body-bold-md content-base-primary">{t("requests.details.assignedUnits")}</h5>
                </div>
                <div className="flex flex-col gap-3">
                  {request.details.request_units.map((unit, unitIndex) => (
                    <div key={unit.id} className="flex flex-col">
                      <div className="flex justify-between items-center">
                        <span className="text-body-regular-md content-base-primary">{unit.item_name}</span>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-body-regular-sm content-base-secondary">
                          {t("requests.details.serialNumber")}: {unit.serial_number}
                        </span>
                      </div>
                      {unitIndex < request.details!.request_units!.length - 1 && (
                        <div className="pt-3 mt-3 border-t surface-base-stroke" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

          {!readOnly && isRevisionRequested(request) && (
            <div className="mt-3 flex flex-col gap-3">
              <Toast
                color="notice"
                text={t(`requests.revisionToast.${request.type}`)}
                closable={false}
                autoClose={false}
                isFullWidth
              />
              {!readOnly && canEditRequest(request) && (
                <Button variant="secondary" onClick={handleEditClick} size="huge" className="w-full">
                  <Edit2 size={16} color="currentColor" />
                  {t("requests.editLeave")}
                </Button>
              )}
              {!readOnly && canEditAdvanceRequest(request) && (
                <Button variant="secondary" onClick={handleEditClick} size="huge" className="w-full">
                  <Edit2 size={16} color="currentColor" />
                  {t("requests.editAdvance")}
                </Button>
              )}
            </div>
          )}

          {!readOnly && canAcknowledgeOrder(request) && (
            <div className="flex pt-3 mt-3 border-t surface-base-stroke">
              <Button
                variant="primary"
                onClick={handleAcknowledgeOrder}
                disabled={isAcknowledging}
                size="huge"
                className="w-full">
                <TickCircle size={16} color="currentColor" />
                {isAcknowledging ? t("requests.acknowledging") : t("requests.acknowledgeOrder")}
              </Button>
            </div>
          )}

          {!readOnly && canCancelItemRequest(request) && (
            <div className="flex pt-3 mt-3 border-t surface-base-stroke">
              <Button
                variant="secondary"
                onClick={handleCancelItemRequest}
                disabled={isCancellingItemRequest}
                size="huge"
                className="w-full">
                <CloseCircle size={16} color="currentColor" />
                {isCancellingItemRequest ? t("requests.cancellingItemRequest") : t("requests.cancelItemRequest")}
              </Button>
            </div>
          )}

          {!readOnly && canCancelDataChangeRequest(request) && (
            <div className="flex pt-3 mt-3 border-t surface-base-stroke">
              <Button
                variant="secondary"
                onClick={() => setConfirmCancelDataChange(true)}
                disabled={isCancellingDataChange}
                size="huge"
                className="w-full">
                <CloseCircle size={16} color="currentColor" />
                {isCancellingDataChange ? t("requests.cancellingDataChange") : t("requests.cancelDataChange")}
              </Button>
            </div>
          )}

          </div>
        </ScrollArea>

        <ScrollArea className="border-l surface-base-stroke pl-6 min-h-0">
          <RequestAuditTimeline requestType={request.type} requestId={request.id} kind={request.details?.kind} />
        </ScrollArea>
      </div>

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={handlePromptClose}
          namespace="EmployeePortal"
        />
      )}

      {!readOnly && confirmDelete && (
        <PromptForm
          title={t("requests.deleteConfirmTitle")}
          text={t("requests.deleteConfirmText")}
          variant="error"
          onClose={() => setConfirmDelete(false)}
          onConfirm={handleDelete}
          isLoading={isDeleting}
          namespace="EmployeePortal"
        />
      )}

      {!readOnly && confirmCancelDataChange && (
        <PromptForm
          title={t("requests.cancelDataChangeConfirmTitle")}
          text={t("requests.cancelDataChangeConfirmText")}
          variant="warning"
          onClose={() => setConfirmCancelDataChange(false)}
          onConfirm={handleCancelDataChange}
          isLoading={isCancellingDataChange}
          namespace="EmployeePortal"
        />
      )}
    </ModalWrapper>
  );
}
